import {
  Action,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import SupportedContext from '@/types/SupportedContext';
import SearchDefaults from '@/types/SearchDefaults';
import FilterState from '@/store/states/Filter';
import FilterContextData from '@/models/FilterContextData';
import Filter from '@/models/Filter';
import FilterOption from '@/models/FilterOption';
import FilterSerializedOptions from '@/models/FilterSerializedOptions';
import SearchResponse from '@/requests/SearchResponse';
import RouteData from '@/models/RouteData';
import { FilterGetter } from '@/store/getters';
import { FilterAction, GeneralAction } from '@/store/actions';
import FilterResponse from '@/models/FilterResponse';
import QueryString from 'qs';
import InitDataExtended from '@/models/InitDataExtended';

enum Mutators {
  HandleSetSideBarVisibility = 'filter.handleSetSideBarVisibility',
  HandleSetInitFilterSerializedOptions = 'filter.handleSetInitFilterSerializedOptions',
  HandleUpdateSearchResults = 'filter.handleUpdateSearchResults',
  HandleSearchResponse = 'filter.handleSearchResponse',
  HandleSwitchSearchContext = 'filter.handleSwitchSearchContext',
  HandleChangeDisplayFilters = 'filter.handleChangeDisplayFilters',
  HandleToggleFilterOption = 'filter.handleToggleFilterOption',
  HandleSetAppliedFilterOptions = 'filter.handleSetAppliedFilterOptions',
  HandleRemoveAllAppliedFilterOptions = 'filter.handleRemoveAllAppliedFilterOptions',
  HandleShowMore = 'filter.handleShowMore',
  HandleAccordion = 'filter.handleAccordion',
}

@Module
export default class FilterModule extends VuexModule<FilterState> {
  private sideBarVisibility: boolean;

  private activeSearchContext: SupportedContext;

  private readonly searchContexts: Array<SupportedContext>;

  private searchContextsData: Map<SupportedContext, FilterContextData>;

  private filters: Map<string, Filter>;

  private appliedFilterOptions: Map<string, FilterOption>;

  private initFilterSerializedOptions: FilterSerializedOptions = {};

  private displayFilters: boolean;

  constructor(module: FilterModule) {
    super(module);

    this.sideBarVisibility = false;
    this.activeSearchContext = SearchDefaults.searchContext;
    this.searchContexts = Object.values(SupportedContext);
    this.searchContextsData = Object.values(SupportedContext).reduce((
      searchContextsAccum: Map<SupportedContext, FilterContextData>,
      searchContext: SupportedContext,
    ) => {
      searchContextsAccum.set(searchContext, {
        loading: true,
        numberOfResults: 0,
      });
      return searchContextsAccum;
    }, new Map());
    this.filters = new Map();
    this.appliedFilterOptions = new Map();
    this.displayFilters = SearchDefaults.displayFilters;
    // this.appliedFilters = new Map();
  }

  get [FilterGetter.SideBarVisibility](): boolean {
    return this.sideBarVisibility;
  }

  get [FilterGetter.ActiveSearchContext](): SupportedContext {
    return this.activeSearchContext;
  }

  get [FilterGetter.SearchContexts](): Array<SupportedContext> {
    return this.searchContexts;
  }

  get [FilterGetter.SearchContextsData](): Map<SupportedContext, FilterContextData> {
    return this.searchContextsData;
  }

  get [FilterGetter.Filters](): Map<string, Filter> {
    return this.filters;
  }

  get [FilterGetter.AppliedFilterOptions](): Map<string, FilterOption> {
    return this.appliedFilterOptions;
  }

  get [FilterGetter.AppliedFilterOptionsSerialized](): FilterSerializedOptions {
    const serializedOptions: FilterSerializedOptions = {};
    this.appliedFilterOptions.forEach((filterOption: FilterOption) => {
      if (serializedOptions[filterOption.filterId] !== undefined) {
        serializedOptions[filterOption.filterId] += `,${filterOption.id}`;
      } else {
        serializedOptions[filterOption.filterId] = filterOption.id;
      }
    });
    return serializedOptions;
  }

  get [FilterGetter.DisplayFilters](): boolean {
    return this.displayFilters;
  }

  private static buildFilterOptions(
    filters: Map<string, Filter>,
    filterSerializedOptions: FilterSerializedOptions,
  ) {
    const appliedFilterOptions: Map<string, FilterOption> = new Map();
    Object.keys(filterSerializedOptions).forEach((filterId: string) => {
      const filter: Filter | undefined = filters.get(filterId);
      if (filter) {
        const optionIds: Array<string> = filterSerializedOptions[filterId].split(',');
        optionIds.forEach((optionId: string) => {
          const option: FilterOption | undefined = filter.options.get(optionId);
          if (option) {
            appliedFilterOptions.set(option.id, option);
          }
        });
      }
    });
    return appliedFilterOptions;
  }

  @Mutation
  private [Mutators.HandleSetSideBarVisibility](payload: { isOpen: boolean }) {
    this.sideBarVisibility = payload.isOpen;
  }

  @Action
  private [FilterAction.SetSideBarVisibility](payload: { isOpen: boolean }) {
    this.context.commit(Mutators.HandleSetSideBarVisibility, payload);
  }

  @Mutation
  private [Mutators.HandleSwitchSearchContext](payload: { searchContext: SupportedContext }) {
    this.activeSearchContext = payload.searchContext;
    this.filters = new Map();
    this.appliedFilterOptions = new Map();
  }

  @Action
  private async [FilterAction.SwitchSearchContext](payload: { searchContext: SupportedContext }) {
    this.context.commit(Mutators.HandleSwitchSearchContext, payload);
    await this.context.dispatch(GeneralAction.SwitchSearchContext, payload);
  }

  @Mutation
  private [Mutators.HandleSetInitFilterSerializedOptions](
    payload: { filterSerializedOptions: FilterSerializedOptions },
  ) {
    this.initFilterSerializedOptions = payload.filterSerializedOptions;
  }

  @Action
  private [GeneralAction.Init](payload: InitDataExtended) {
    this.context.commit(Mutators.HandleSwitchSearchContext, {
      searchContext: payload.searchContext,
    });

    this.context.commit(Mutators.HandleChangeDisplayFilters, {
      displayFilters: payload.displayFilters,
    });

    if (Object.keys(payload.filterSerializedOptions).length > 0) {
      this.context.commit(Mutators.HandleSetInitFilterSerializedOptions, {
        filterSerializedOptions: payload.filterSerializedOptions,
      });
    }
  }

  @Mutation
  private [Mutators.HandleChangeDisplayFilters](
    payload: { displayFilters: boolean },
  ) {
    this.displayFilters = payload.displayFilters;
  }

  @Mutation
  private [Mutators.HandleSetAppliedFilterOptions](
    payload: { appliedFilterOptions: Map<string, FilterOption> },
  ) {
    this.appliedFilterOptions = new Map(payload.appliedFilterOptions);
  }

  @Action
  private async [GeneralAction.RouteUpdate](payload: { routeData: RouteData }) {
    if (this.activeSearchContext !== payload.routeData.searchContext) {
      this.context.commit(Mutators.HandleSwitchSearchContext, {
        searchContext: payload.routeData.searchContext,
      });
    }

    if (
      QueryString.stringify(this[FilterGetter.AppliedFilterOptionsSerialized])
      !== QueryString.stringify(payload.routeData.filterSerializedOptions)
    ) {
      this.context.commit(Mutators.HandleSetAppliedFilterOptions, {
        appliedFilterOptions: FilterModule.buildFilterOptions(
          this.filters,
          payload.routeData.filterSerializedOptions,
        ),
      });
      await this.context.dispatch(GeneralAction.SwitchFilters);
    }
  }

  @Mutation
  private [Mutators.HandleUpdateSearchResults](
    payload: {
      responseData: SearchResponse;
    },
  ) {
    this.searchContexts.forEach((searchContext: SupportedContext) => {
      this.searchContextsData.set(searchContext, {
        loading: false,
        numberOfResults: payload.responseData.contexts[searchContext] || 0,
      });
    });
    this.searchContextsData = new Map(this.searchContextsData);
  }

  @Action
  private async [GeneralAction.UpdateSearchResults](payload: {
    responseData: SearchResponse;
  }) {
    this.context.commit(Mutators.HandleUpdateSearchResults, payload);
  }

  @Mutation
  private [Mutators.HandleSearchResponse](
    payload: {
      responseData: SearchResponse;
    },
  ) {
    let filterCounter = 0;
    const appliedFilterIds: Array<string> = [];
    this.appliedFilterOptions.forEach((opt: FilterOption) => {
      appliedFilterIds.push(opt.filterId);
    });

    const filters = new Map(payload.responseData.filters.reduce((
      filterAccum: Map<string, Filter>,
      filterResponse: FilterResponse,
    ) => {
      filterAccum.set(filterResponse.id, {
        id: filterResponse.id,
        text: filterResponse.text,
        total: filterResponse.total,
        isOpen: filterCounter < SearchDefaults.sidebar.filter.limit
            || this.filters.get(filterResponse.id)?.isOpen
            || appliedFilterIds.includes(filterResponse.id)
            || false,
        isExpanded: this.filters.get(filterResponse.id)?.isExpanded || false,
        options: filterResponse.options.reduce((
          optionAccum: Map<string, FilterOption>,
          filterOption: FilterOption,
        ) => {
          optionAccum.set(filterOption.id, {
            ...filterOption,
            isChecked: this.appliedFilterOptions.has(filterOption.id),
          });
          return optionAccum;
        }, new Map()),
      });
      filterCounter += 1;
      return filterAccum;
    }, new Map()));
    this.filters = filters;
    const updatedAppliedFilterOptions = new Map();
    this.appliedFilterOptions.forEach((filterOption: FilterOption, key: string) => {
      if (this.filters.get(filterOption.filterId)?.options.has(filterOption.id)
      ) {
        updatedAppliedFilterOptions.set(key, filterOption);
      }
    });
    this.appliedFilterOptions = new Map(updatedAppliedFilterOptions);
  }

  @Action
  private async [GeneralAction.HandleSearchResponse](payload: {
    responseData: SearchResponse;
  }) {
    const initialAppliedFilterOptions = this.appliedFilterOptions;
    this.context.commit(Mutators.HandleSearchResponse, payload);

    let triggerSwitchFilters = initialAppliedFilterOptions.size !== this.appliedFilterOptions.size;

    if (Object.keys(this.initFilterSerializedOptions).length > 0) {
      this.context.commit(Mutators.HandleSetAppliedFilterOptions, {
        appliedFilterOptions: FilterModule.buildFilterOptions(
          this.filters,
          this.initFilterSerializedOptions,
        ),
      });
      this.context.commit(Mutators.HandleSetInitFilterSerializedOptions, {
        filterSerializedOptions: {},
      });
      triggerSwitchFilters = true;
    }

    if (triggerSwitchFilters) {
      await this.context.dispatch(GeneralAction.SwitchFilters);
    } else {
      await this.context.dispatch(GeneralAction.UpdateSearchResults, payload);
    }
  }

  @Mutation
  private [Mutators.HandleToggleFilterOption](payload: { filterOption: FilterOption }) {
    const filter: Filter | undefined = this.filters.get(
      payload.filterOption.filterId,
    );
    if (filter) {
      const option: FilterOption | undefined = filter.options.get(payload.filterOption.id);
      if (option) {
        option.isChecked = !this.appliedFilterOptions.has(payload.filterOption.id);
      }

      if (this.appliedFilterOptions.has(payload.filterOption.id)) {
        this.appliedFilterOptions.delete(payload.filterOption.id);
      } else {
        this.appliedFilterOptions.set(
          payload.filterOption.id,
          payload.filterOption,
        );
      }
      this.appliedFilterOptions = new Map(this.appliedFilterOptions);
      this.filters = new Map(this.filters);
    }
  }

  @Action
  private async [FilterAction.ToggleFilterOption](payload: { filterOption: FilterOption }) {
    this.context.commit(Mutators.HandleToggleFilterOption, payload);
    await this.context.dispatch(GeneralAction.SwitchFilters);
  }

  @Mutation
  private [Mutators.HandleRemoveAllAppliedFilterOptions]() {
    this.appliedFilterOptions = new Map();
  }

  @Action
  private async [FilterAction.RemoveAllAppliedFilterOptions]() {
    this.context.commit(Mutators.HandleRemoveAllAppliedFilterOptions);
    await this.context.dispatch(GeneralAction.SwitchFilters);
  }

  @Mutation
  private [Mutators.HandleShowMore](payload: { filter: Filter }) {
    this.filters.set(
      payload.filter.id,
      { ...payload.filter, isExpanded: !payload.filter.isExpanded },
    );
    this.filters = new Map(this.filters);
  }

  @Action
  private [FilterAction.ToggleShowMore](payload: { filter: Filter }) {
    this.context.commit(Mutators.HandleShowMore, payload);
  }

  @Mutation
  private [Mutators.HandleAccordion](payload: { filter: Filter }) {
    this.filters.set(
      payload.filter.id,
      { ...payload.filter, isOpen: !payload.filter.isOpen },
    );
    this.filters = new Map(this.filters);
  }

  @Action
  private [FilterAction.ToggleAccordion](payload: { filter: Filter }) {
    this.context.commit(Mutators.HandleAccordion, payload);
  }
}
